import { useEffect, useState, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/lib/store'
import { Toast } from '@/components/ui/toast'
import { Heart, Share2, ShoppingCart, AlertCircle, Trash2, MessageSquare, Send, User, Eye } from 'lucide-react'
import { BackButton } from '@/components/ui/back-button'

interface VideoDetails {
  id: string
  user_id: string
  title: string
  video_url: string
  thumbnail_url: string | null
  product_id: string | null
  status: 'pending' | 'approved' | 'rejected'
  likes_count: number
  created_at: string
  updated_at: string | null
  description: string | null
  profiles?: {
    full_name: string | null
    avatar_url: string | null
  } | null
  products?: {
    id: string
    title: string
    price: number
    images: string[]
  } | null
}

interface Comment {
  id: string
  content: string
  created_at: string
  user_id: string
  user: {
    full_name: string | null
    avatar_url: string | null
  } | null
}

export function VideoDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [video, setVideo] = useState<VideoDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [isLiked, setIsLiked] = useState(false)
  const [likeLoading, setLikeLoading] = useState(false)
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [commentLoading, setCommentLoading] = useState(false)
  const [viewsCount, setViewsCount] = useState(0)
  const commentsEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (id) {
      fetchVideo()
      fetchComments()
      fetchViewCount()
      incrementViews()
      if (user) {
        checkIfLiked()
      }
    }
  }, [id, user])

  useEffect(() => {
    // Scroll to bottom of comments when new ones are added
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [comments])

  const fetchVideo = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('videos')
        .select('*, profiles(full_name, avatar_url), products(id, title, price, images)')
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError
      setVideo(data)
    } catch (err) {
      setError('Failed to load video')
      console.error('Error fetching video:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchComments = async () => {
    try {
      const { data, error } = await supabase
        .from('video_comments')
        .select(`
          id,
          content,
          created_at,
          user_id,
          user:profiles(full_name, avatar_url)
        `)
        .eq('video_id', id)
        .order('created_at', { ascending: true })

      if (error) throw error
      setComments(data || [])
    } catch (err) {
      console.error('Error fetching comments:', err)
    }
  }

  const fetchViewCount = async () => {
    try {
      const { data, error } = await supabase
        .from('video_views')
        .select('count')
        .eq('video_id', id)
        .maybeSingle()

      if (error) throw error
      setViewsCount(data?.count || 0)
    } catch (err) {
      console.error('Error fetching view count:', err)
    }
  }

  const checkIfLiked = async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('video_likes')
        .select('id')
        .eq('video_id', id)
        .eq('user_id', user.id)
        .maybeSingle()

      if (error) throw error
      setIsLiked(!!data)
    } catch (err) {
      console.error('Error checking like status:', err)
    }
  }

  const incrementViews = async () => {
    try {
      // First check if the video_views record exists
      const { data, error } = await supabase
        .from('video_views')
        .select('count')
        .eq('video_id', id)
        .maybeSingle()

      if (error) throw error

      if (data) {
        // Update existing record
        const newCount = data.count + 1
        const { error: updateError } = await supabase
          .from('video_views')
          .update({ count: newCount })
          .eq('video_id', id)
        
        if (updateError) throw updateError
        setViewsCount(newCount)
      } else {
        // Create new record using upsert to handle potential race conditions
        const { error: insertError } = await supabase
          .from('video_views')
          .upsert([{ video_id: id, count: 1 }], { onConflict: 'video_id' })
        
        if (insertError) throw insertError
        setViewsCount(1)
      }
    } catch (err) {
      console.error('Error incrementing views:', err)
    }
  }

  const handleDeleteVideo = async () => {
    if (!video || !user) return

    try {
      setDeleteLoading(true)
      
      // Check if user is the owner or admin
      const isOwner = video.user_id === user.id
      const isAdmin = user.role === 'admin'
      
      if (!isOwner && !isAdmin) {
        throw new Error('You do not have permission to delete this video')
      }

      // Delete the video
      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', video.id)

      if (error) throw error

      // Show success message
      setToastMessage('Vidéo supprimée avec succès')
      setToastType('success')
      setShowToast(true)
      
      // Redirect after a delay
      setTimeout(() => {
        navigate('/videos')
      }, 2000)
    } catch (err) {
      console.error('Error deleting video:', err)
      setToastMessage('Erreur lors de la suppression de la vidéo')
      setToastType('error')
      setShowToast(true)
      setShowDeleteConfirm(false)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleLike = async () => {
    if (!user) {
      setToastMessage('Veuillez vous connecter pour aimer cette vidéo')
      setToastType('error')
      setShowToast(true)
      return
    }

    if (!video) return

    try {
      setLikeLoading(true)

      if (isLiked) {
        // Unlike the video
        const { error } = await supabase
          .from('video_likes')
          .delete()
          .eq('video_id', video.id)
          .eq('user_id', user.id)

        if (error) throw error

        // Update video likes count
        const newLikesCount = Math.max(0, (video.likes_count || 0) - 1)
        await supabase
          .from('videos')
          .update({ likes_count: newLikesCount })
          .eq('id', video.id)

        setVideo(prev => prev ? { ...prev, likes_count: newLikesCount } : null)
        setIsLiked(false)
      } else {
        // Like the video
        const { error } = await supabase
          .from('video_likes')
          .insert([{ video_id: video.id, user_id: user.id }])

        if (error) throw error

        // Update video likes count
        const newLikesCount = (video.likes_count || 0) + 1
        await supabase
          .from('videos')
          .update({ likes_count: newLikesCount })
          .eq('id', video.id)

        setVideo(prev => prev ? { ...prev, likes_count: newLikesCount } : null)
        setIsLiked(true)
      }
    } catch (err) {
      console.error('Error updating like:', err)
      setToastMessage('Une erreur est survenue')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLikeLoading(false)
    }
  }

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user) {
      setToastMessage('Veuillez vous connecter pour commenter')
      setToastType('error')
      setShowToast(true)
      return
    }

    if (!newComment.trim()) return

    try {
      setCommentLoading(true)

      const { data, error } = await supabase
        .from('video_comments')
        .insert([{
          video_id: id,
          user_id: user.id,
          content: newComment
        }])
        .select(`
          id,
          content,
          created_at,
          user_id,
          user:profiles(full_name, avatar_url)
        `)
        .single()

      if (error) throw error

      setComments(prev => [...prev, data])
      setNewComment('')
    } catch (err) {
      console.error('Error adding comment:', err)
      setToastMessage('Erreur lors de l\'ajout du commentaire')
      setToastType('error')
      setShowToast(true)
    } finally {
      setCommentLoading(false)
    }
  }

  const handleShareVideo = () => {
    if (navigator.share) {
      navigator.share({
        title: video?.title || 'Shared video',
        url: window.location.href
      }).catch(err => console.error('Error sharing:', err))
    } else {
      navigator.clipboard.writeText(window.location.href)
        .then(() => {
          setToastMessage('Lien copié dans le presse-papier')
          setToastType('success')
          setShowToast(true)
        })
        .catch(err => {
          console.error('Error copying to clipboard:', err)
          setToastMessage('Erreur lors de la copie du lien')
          setToastType('error')
          setShowToast(true)
        })
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="aspect-video bg-gray-200 rounded-lg" />
          <div className="mt-4 h-8 bg-gray-200 rounded w-3/4" />
          <div className="mt-2 h-4 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    )
  }

  if (error || !video) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error || 'Video not found'}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const canDelete = user && (user.id === video.user_id || user.role === 'admin')

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">
                Confirmer la suppression
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Êtes-vous sûr de vouloir supprimer cette vidéo ? Cette action est irréversible.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Annuler
              </Button>
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={handleDeleteVideo}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Suppression...' : 'Supprimer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <BackButton to="/videos" label="Retour aux vidéos" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="aspect-video rounded-lg overflow-hidden bg-black">
            {video.video_url.includes('youtube.com') || video.video_url.includes('youtu.be') ? (
              <iframe
                src={video.video_url.replace('watch?v=', 'embed/')}
                title={video.title}
                className="w-full h-full"
                allowFullScreen
              />
            ) : (
              <video
                src={video.video_url}
                controls
                className="w-full h-full"
                poster={video.thumbnail_url || undefined}
              />
            )}
          </div>

          <div className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{video.title}</h1>
                <div className="mt-2 flex flex-wrap items-center">
                  <div className="flex items-center mr-4 mb-2 sm:mb-0">
                    <div className="h-8 w-8 rounded-full bg-gray-200 overflow-hidden">
                      {video.profiles?.avatar_url ? (
                        <img 
                          src={video.profiles.avatar_url} 
                          alt={video.profiles.full_name || 'User'} 
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <User className="h-5 w-5 text-gray-500 m-1.5" />
                      )}
                    </div>
                    <span className="ml-2 text-sm text-gray-700">
                      {video.profiles?.full_name || 'Anonymous'}
                    </span>
                  </div>
                  <span className="mx-2 text-gray-300 hidden sm:inline">•</span>
                  <span className="text-sm text-gray-500">
                    {new Date(video.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleLike}
                  disabled={likeLoading}
                  className={isLiked ? 'text-red-600 border-red-200 bg-red-50' : ''}
                >
                  <Heart className={`h-4 w-4 mr-2 ${isLiked ? 'fill-current' : ''}`} />
                  {video.likes_count || 0}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleShareVideo}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Partager
                </Button>
                {canDelete && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer
                  </Button>
                )}
              </div>
            </div>

            {video.description && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{video.description}</p>
              </div>
            )}

            <div className="mt-4 flex items-center text-sm text-gray-500">
              <Eye className="h-4 w-4 mr-1" />
              <span>{viewsCount} vues</span>
            </div>
          </div>

          {/* Comments Section */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Commentaires ({comments.length})
            </h2>
            
            {user ? (
              <form onSubmit={handleAddComment} className="mb-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 hidden sm:block">
                    <div className="h-10 w-10 rounded-full bg-gray-200 overflow-hidden">
                      {user.avatar_url ? (
                        <img 
                          src={user.avatar_url} 
                          alt={user.full_name || 'User'} 
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <User className="h-6 w-6 text-gray-500 m-2" />
                      )}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="border border-gray-300 rounded-lg shadow-sm overflow-hidden focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
                      <textarea
                        rows={3}
                        name="comment"
                        id="comment"
                        className="block w-full py-3 border-0 resize-none focus:ring-0 sm:text-sm"
                        placeholder="Ajouter un commentaire..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        required
                      />
                      <div className="py-2 px-3 border-t border-gray-200 flex justify-end">
                        <Button
                          type="submit"
                          variant="primary"
                          size="sm"
                          disabled={commentLoading || !newComment.trim()}
                        >
                          {commentLoading ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Commenter
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-700">
                  <Link to="/login" className="text-primary hover:underline">Connectez-vous</Link> pour ajouter un commentaire
                </p>
              </div>
            )}

            <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
              {comments.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <MessageSquare className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">Aucun commentaire pour le moment</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="flex space-x-4">
                    <div className="flex-shrink-0 hidden sm:block">
                      <div className="h-10 w-10 rounded-full bg-gray-200 overflow-hidden">
                        {comment.user?.avatar_url ? (
                          <img 
                            src={comment.user.avatar_url} 
                            alt={comment.user.full_name || 'User'} 
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <User className="h-6 w-6 text-gray-500 m-2" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap">
                        <h3 className="text-sm font-medium text-gray-900">{comment.user?.full_name || 'Anonymous'}</h3>
                        <span className="ml-2 text-xs text-gray-500">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-700 break-words">
                        <p>{comment.content}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={commentsEndRef} />
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          {video.products && (
            <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Produit associé</h2>
              </div>
              <div className="p-4">
                <div className="flex items-center">
                  <div className="h-16 w-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                    {video.products.images?.[0] ? (
                      <img
                        src={video.products.images[0]}
                        alt={video.products.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <ShoppingCart className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="ml-4 flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {video.products.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      ${video.products.price}
                    </p>
                  </div>
                </div>
                <div className="mt-4">
                  <Link to={`/products/${video.products.id}`}>
                    <Button
                      variant="primary"
                      className="w-full"
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Voir le produit
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Statistiques</h2>
            </div>
            <div className="p-4">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Vues</span>
                  <span className="text-sm font-medium text-gray-900">{viewsCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">J'aime</span>
                  <span className="text-sm font-medium text-gray-900">{video.likes_count || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Commentaires</span>
                  <span className="text-sm font-medium text-gray-900">{comments.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Publié le</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date(video.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}